import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useStore } from '@/context/StoreContext';
import { MEDIA } from '@/data/site';
import { navigate } from '@/hooks/useRoute';
import { createHypPayment } from '@/services/paymentService';
import { getCurrentUser } from '@/lib/supabase';
import { trackMielleyEvent } from '@/lib/mielleyAnalytics';
import type { CheckoutAddress, CheckoutCustomer, FulfillmentMethod } from '@/types/payment';

const money = (n: number) => new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  maximumFractionDigits: 0,
}).format(n);

const emptyCustomer: CheckoutCustomer = { firstName: '', lastName: '', phone: '', email: '' };
const emptyAddress: CheckoutAddress = { street: '', city: '', zip: '' };

const validEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());
const validPhone = (value: string) => value.replace(/\D/g, '').length >= 9;

export function CheckoutPage() {
  const { cart, products } = useStore();
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState<CheckoutCustomer>(emptyCustomer);
  const [fulfillment, setFulfillment] = useState<FulfillmentMethod>('delivery');
  const [address, setAddress] = useState<CheckoutAddress>(emptyAddress);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');


  useEffect(() => {
    let alive = true;
    void getCurrentUser().then(current => {
      if (!alive || !current) return;
      const metadata = current.user_metadata || {};
      const fullName = String(metadata.full_name || '').trim();
      const parts = fullName.split(/\s+/).filter(Boolean);
      setCustomer(prev => ({
        firstName: prev.firstName || parts[0] || '',
        lastName: prev.lastName || parts.slice(1).join(' '),
        phone: prev.phone || String(metadata.phone || ''),
        email: prev.email || String(current.email || ''),
      }));
    }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const subtotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.qty, 0), [cart]);
  const beginCheckoutTracked = useRef(false);

useEffect(() => {
  if (!cart.length || beginCheckoutTracked.current) return;

  beginCheckoutTracked.current = true;

  void trackMielleyEvent({
    eventName: 'begin_checkout',
    path: window.location.pathname + window.location.search,

    metadata: {
      currency: 'ILS',
      value: subtotal,

      items: cart.map(item => ({
        item_id: String(item.id),
        price: Number(item.price),
        quantity: Number(item.qty),
      })),
    },
  });
}, [cart, subtotal]);

  if (!cart.length) {
    return <section className="section empty"><h1>הסל ריק</h1><button className="btn gold" onClick={() => navigate('/catalog')}>לחנות</button></section>;
  }

  const updateCustomer = (field: keyof CheckoutCustomer, value: string) => setCustomer(prev => ({ ...prev, [field]: value }));
  const updateAddress = (field: keyof CheckoutAddress, value: string) => setAddress(prev => ({ ...prev, [field]: value }));

  const nextFromContact = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!customer.firstName.trim() || !customer.lastName.trim()) return setError('יש למלא שם פרטי ושם משפחה.');
    if (!validPhone(customer.phone)) return setError('יש להזין מספר טלפון תקין.');
    if (!validEmail(customer.email)) return setError('יש להזין כתובת אימייל תקינה.');
    setStep(2);
  };

  const nextFromShipping = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (fulfillment === 'delivery' && (!address.street.trim() || !address.city.trim())) {
      return setError('למשלוח עד הבית יש למלא כתובת ועיר.');
    }
    setStep(3);
  };

  const pay = async () => {
    if (!termsAccepted) return setError('יש לאשר את תנאי השימוש ומדיניות הפרטיות.');
    setBusy(true);
    setError('');
    try {
      const result = await createHypPayment({
        customer: {
          firstName: customer.firstName.trim(),
          lastName: customer.lastName.trim(),
          phone: customer.phone.trim(),
          email: customer.email.trim().toLowerCase(),
        },
        fulfillment,
        address: fulfillment === 'delivery' ? {
          street: address.street.trim(),
          city: address.city.trim(),
          zip: address.zip.trim(),
        } : undefined,
        cart: cart.map(item => ({ id: item.id, qty: item.qty, options: item.options })),
      });

      sessionStorage.setItem('mielley_pending_order', JSON.stringify({
  orderNumber: result.orderNumber,
  createdAt: new Date().toISOString(),

  currency: 'ILS',
  value: subtotal,

  items: cart.map(item => ({
    item_id: String(item.id),
    price: Number(item.price),
    quantity: Number(item.qty),
  })),
}));
      window.location.assign(result.paymentUrl);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'לא ניתן להתחיל את התשלום כרגע.');
    }
  };

  return <>
    <header className="checkout-header">
      <a href="/" onClick={event => { event.preventDefault(); navigate('/'); }}><img src={MEDIA.logo} alt="MIELLEY" /></a>
      <span>✓ תשלום מאובטח</span>
    </header>
    <main className="checkout mielley-secure-checkout" dir="rtl">
      <div className="container">
        <button className="link-button" onClick={() => navigate('/cart')}>← חזרה לסל</button>
        <div className="checkout-grid">
          <section className="checkout-card">
            <div className="checkout-steps">
              {['פרטים', 'משלוח', 'תשלום'].map((label, index) => <span className={step === index + 1 ? 'active' : step > index + 1 ? 'done' : ''} key={label}>{step > index + 1 ? '✓' : index + 1} {label}</span>)}
            </div>

            {error && <div className="checkout-error" role="alert">{error}</div>}

            {step === 1 && <form onSubmit={nextFromContact}>
              <div className="checkout-title"><small>01</small><div><h2>פרטי קשר</h2><p>נשתמש בפרטים האלה לאישור ההזמנה ולעדכונים בלבד.</p></div></div>
              <div className="form-grid">
                <label>שם פרטי<input autoComplete="given-name" value={customer.firstName} onChange={e => updateCustomer('firstName', e.target.value)} /></label>
                <label>שם משפחה<input autoComplete="family-name" value={customer.lastName} onChange={e => updateCustomer('lastName', e.target.value)} /></label>
                <label>טלפון<input type="tel" inputMode="tel" autoComplete="tel" value={customer.phone} onChange={e => updateCustomer('phone', e.target.value)} /></label>
                <label>אימייל<input type="email" autoComplete="email" value={customer.email} onChange={e => updateCustomer('email', e.target.value)} /></label>
              </div>
              <button className="btn gold checkout-primary" type="submit">המשך למשלוח</button>
            </form>}

            {step === 2 && <form onSubmit={nextFromShipping}>
              <div className="checkout-title"><small>02</small><div><h2>משלוח או איסוף</h2><p>בחרו את הדרך שנוחה לכם לקבל את התכשיטים.</p></div></div>
              <div className="shipping-options premium-shipping">
                <label className={fulfillment === 'delivery' ? 'selected' : ''}><input type="radio" name="shipping" checked={fulfillment === 'delivery'} onChange={() => setFulfillment('delivery')} /><span><b>משלוח מבוטח עד הבית</b><small>ללא עלות</small></span></label>
                <label className={fulfillment === 'pickup' ? 'selected' : ''}><input type="radio" name="shipping" checked={fulfillment === 'pickup'} onChange={() => setFulfillment('pickup')} /><span><b>איסוף עצמי</b><small>בתיאום מראש</small></span></label>
              </div>
              {fulfillment === 'delivery' && <div className="form-grid checkout-address">
                <label className="full">כתובת<input autoComplete="street-address" value={address.street} onChange={e => updateAddress('street', e.target.value)} /></label>
                <label>עיר<input autoComplete="address-level2" value={address.city} onChange={e => updateAddress('city', e.target.value)} /></label>
                <label>מיקוד<input inputMode="numeric" autoComplete="postal-code" value={address.zip} onChange={e => updateAddress('zip', e.target.value)} /></label>
              </div>}
              <div className="form-actions"><button className="btn" type="button" onClick={() => { setError(''); setStep(1); }}>חזרה</button><button className="btn gold" type="submit">המשך לתשלום</button></div>
            </form>}

            {step === 3 && <>
              <div className="checkout-title"><small>03</small><div><h2>תשלום מאובטח</h2><p>בדקו שהכול נכון. בלחיצה על הכפתור תועברו לדף התשלום המאובטח של HYP.</p></div></div>
              <div className="hyp-payment-box">
                <div className="hyp-payment-lock" aria-hidden="true">⌁</div>
                <div><b>פרטי הכרטיס אינם נשמרים ב-MIELLEY</b><p>התשלום מתבצע מחוץ לאתר בדף המאובטח של HYP / MAX, עם 3DS ואמצעי התשלום שהופעלו במסוף.</p></div>
                <div className="payment-badges"><span>HYP</span><span>MAX</span><span>3DS</span></div>
              </div>
              <div className="checkout-review">
                <p><span>לקוחה</span><b>{customer.firstName} {customer.lastName}</b></p>
                <p><span>טלפון</span><b>{customer.phone}</b></p>
                <p><span>קבלה</span><b>{fulfillment === 'delivery' ? `${address.street}, ${address.city}` : 'איסוף עצמי בתיאום'}</b></p>
                <p><span>סה״כ לתשלום</span><strong>{money(subtotal)}</strong></p>
              </div>
              <label className="checkout-terms"><input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} /><span>קראתי ואני מאשר/ת את <a href="/terms" onClick={e => { e.preventDefault(); navigate('/terms'); }}>תנאי השימוש</a> ואת <a href="/privacy" onClick={e => { e.preventDefault(); navigate('/privacy'); }}>מדיניות הפרטיות</a>.</span></label>
              <div className="form-actions"><button className="btn" type="button" disabled={busy} onClick={() => { setError(''); setStep(2); }}>חזרה</button><button className="btn gold secure-pay-button" type="button" disabled={busy} onClick={() => void pay()}>{busy ? 'פותחים דף תשלום מאובטח…' : `מעבר לתשלום מאובטח · ${money(subtotal)}`}</button></div>
              <small className="server-price-note">הסכום הסופי מחושב שוב בשרת לפי מחירי המוצרים הפעילים לפני יצירת העסקה.</small>
            </>}
          </section>

          <aside className="order-summary">
            <h3>סיכום הזמנה</h3>
            {cart.map((item, index) => {
              const product = products.find(x => x.id === item.id);
              return product ? <article key={`${item.id}-${index}`}><img src={product.image} alt="" /><div><b>{product.name}</b><small>כמות {item.qty}</small>{item.options && Object.values(item.options).filter(Boolean).length > 0 && <small>{Object.values(item.options).filter(Boolean).slice(0, 3).join(' · ')}</small>}</div><strong>{money(item.price * item.qty)}</strong></article> : null;
            })}
            <p><span>משלוח</span><b>חינם</b></p>
            <p className="total"><span>לתשלום</span><b>{money(subtotal)}</b></p>
            <div className="order-trust"><span>✓ תשלום מאובטח</span><span>✓ מחיר מאומת בשרת</span><span>✓ לא שומרים פרטי אשראי</span></div>
          </aside>
        </div>
      </div>
    </main>
  </>;
}
